// React
import React, { useEffect } from 'react';

// Packages
import cc from 'classcat';

// Utilities
import {
    useElseware,
    useLabels,
    useActivityTagging,
    useContext,
} from 'utilities/hooks';
import { useInitiativeDataStore } from 'utilities/store';
import { CONSTANTS } from 'utilities/configuration';

// Components
import { InputWrapper, FormFields } from 'components/_inputs';
import Metric from '../metric';

const ActivityReflections = ({
    primary = false,
    secondary = false,
    additional = false,
    initComponent,
    form,
    item,
}) => {
    // ///////////////////
    // STORES
    // ///////////////////

    const { utilities } = useInitiativeDataStore();
    const { REPORT_ID } = useContext();

    // ///////////////////
    // STATE
    // ///////////////////

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { ewCreate, ewUpdate } = useElseware();
    const { object, label } = useLabels();
    const tagging = useActivityTagging({
        types: [CONSTANTS.TAGS.INTERVENTION_ACTIVITY],
    });

    // ///////////////////
    // METHODS
    // ///////////////////

    function getMetrics(item) {
        const {
            Primary__Toggle,
            Primary___Current_Status__c,
            Primary___Gender__c,
            Primary___Geographical_Reach__c,
            Primary___Name,
            Primary___Target__c,
            Primary___Toggle___Target__c,
            Secondary__Toggle,
            Secondary___Current_Status__c,
            Secondary___Gender__c,
            Secondary___Geographical_Reach__c,
            Secondary___Name,
            Secondary___Target__c,
            Secondary___Toggle___Target__c,
            Additional__Toggle,
            Additional___Current_Status__c,
            Additional___Gender__c,
            Additional___Geographical_Reach__c,
            Additional___Name,
            Additional___Target__c,
            Additional___Toggle___Target__c,
            Additional___Type__c,
            Additional___CurrencyIsoCode,
        } = item;

        const metrics = {};

        if (primary && Primary__Toggle) {
            metrics.primary = {
                metricType: CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE,
                preTitle: label('ActivityBuilderMetric_PrimaryAudience_Label'),
                Current_Status__c: Primary___Current_Status__c,
                Gender__c: Primary___Gender__c,
                Geographical_Reach__c: Primary___Geographical_Reach__c,
                Name: Primary___Name,
                Target__c: Primary___Target__c,
                Toggle___Target__c: Primary___Toggle___Target__c,
                tags: tagging.primaryMetric.getTagsFromFieldValue(item),
            };
        }

        if (secondary && Secondary__Toggle) {
            metrics.secondary = {
                metricType: CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE,
                preTitle: label(
                    'ActivityBuilderMetric_SecondaryAudience_Label'
                ),
                Current_Status__c: Secondary___Current_Status__c,
                Gender__c: Secondary___Gender__c,
                Geographical_Reach__c: Secondary___Geographical_Reach__c,
                Name: Secondary___Name,
                Target__c: Secondary___Target__c,
                Toggle___Target__c: Secondary___Toggle___Target__c,
                tags: tagging.secondaryMetric.getTagsFromFieldValue(item),
            };
        }

        if (additional && Additional__Toggle) {
            let tags;

            switch (Additional___Type__c) {
                case CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE:
                    tags = tagging.additionalMetricPeople.getTagsFromFieldValue(
                        item
                    );
                    break;
                case CONSTANTS.ACTIVITY_SUCCESS_METRICS.FINANCIAL:
                    tags = tagging.additionalMetricFinancial.getTagsFromFieldValue(
                        item
                    );
                    break;
                case CONSTANTS.ACTIVITY_SUCCESS_METRICS.CUSTOM:
                    tags = tagging.additionalMetricCustom.getTagsFromFieldValue(
                        item
                    );
                    break;
            }

            metrics.additional = {
                metricType: Additional___Type__c,
                preTitle: label('ActivityBuilderMetric_AdditionalMetric_Label'),
                Current_Status__c: Additional___Current_Status__c,
                Gender__c: Additional___Gender__c,
                Geographical_Reach__c: Additional___Geographical_Reach__c,
                Name: Additional___Name,
                Target__c: Additional___Target__c,
                Toggle___Target__c: Additional___Toggle___Target__c,
                Type__c: Additional___Type__c,
                CurrencyIsoCode: Additional___CurrencyIsoCode,
                tags,
            };
        }

        return (
            <>
                {/* Primary */}
                {metrics.primary && <Metric item={metrics.primary} />}
                {/* Secondary */}
                {metrics.secondary && <Metric item={metrics.secondary} />}
                {/* Additional */}
                {metrics.additional && <Metric item={metrics.additional} />}
            </>
        );
    }

    async function addItem(formData) {
        try {
            // Get saved data
            const { Reflection___Description__c, Activity___Id } = formData;

            const { data: itemData } = await ewCreate(
                'initiative-report-detail/initiative-report-detail',
                {
                    Description__c: Reflection___Description__c,
                    Initiative_Activity__c: Activity___Id,
                    Type__c: CONSTANTS.REPORT_DETAILS.ACTIVITY_OVERVIEW,
                    Initiative_Report__c: REPORT_ID,
                }
            );

            // Update main data
            utilities.updateInitiativeData('_reportDetails', itemData);
        } catch (error) {
            console.log(error);
            return error;
        }
    }

    async function editItem(formData) {
        try {
            // Get saved data
            const { Reflection___Description__c, Reflection___Id } = formData;

            if (Reflection___Id) {
                const { data: itemData } = await ewUpdate(
                    'initiative-report-detail/initiative-report-detail',
                    Reflection___Id,
                    {
                        Description__c: Reflection___Description__c,
                    }
                );
                // Update main data
                utilities.updateInitiativeData('_reportDetails', itemData);
            } else {
                await addItem(formData);
            }
        } catch (error) {
            console.log(error);
            return error;
        }
    }

    // ///////////////////
    // EFFECTS
    // ///////////////////

    useEffect(() => {
        initComponent({
            fields,
            methods: {
                add: { reflections: addItem },
                edit: { reflections: editItem },
            },
        });
    }, []);

    // ///////////////////
    // FIELDS
    // ///////////////////

    function fields() {
        return [
            {
                type: 'Reflection',
                name: 'Reflection___Description__c',
                object: {
                    ...object.config(
                        'Initiative_Report_Detail__c.Description__c'
                    ),
                    required: true,
                },
                maxLength: 750,
            },
        ];
    }

    // ///////////////////
    // RETURN
    // ///////////////////

    return (
        <div className="flex flex-col space-y-24">
            {/* Metric */}
            {(primary || secondary || additional) && (
                <>
                    <h4 className={cc(['t-h5 text-blue-100'])}>
                        {label('ActivityBuilderReflections_Metric_Label')}
                    </h4>
                    <div className="flex flex-col space-y-12">
                        {getMetrics(item)}
                    </div>
                </>
            )}
            {/* Fields */}
            <h4 className={cc(['t-h5 text-blue-100'])}>
                {label('ActivityBuilderReflections_Reflections_Label')}
            </h4>
            <div className="p-16 bg-blue-10 rounded-8">
                <InputWrapper>
                    <FormFields
                        {...{
                            fields: fields(),
                            form,
                            theme: 'blue',
                        }}
                    />
                </InputWrapper>
            </div>
        </div>
    );
};

export default ActivityReflections;
